import React, { useEffect, useMemo, useState } from "react";
import s from "./styles.module.scss";
import Button from "../../components/button";
import Input from "../../components/input";
import { useLocation, useNavigate } from "react-router-dom";
import { useSections } from "../../hooks/use-sections";
import PageHeader from "../../components/page-header";
import PageContainer from "../../components/page-container";

const days = [
  { key: "Mon", label: "Mon", bigLabel: "Monday" },
  { key: "Tue", label: "Tue", bigLabel: "Tuesday" },
  { key: "Wed", label: "Wed", bigLabel: "Wednesday" },
  { key: "Thu", label: "Thu", bigLabel: "Thursday" },
  { key: "Fri", label: "Fri", bigLabel: "Friday" },
  { key: "Sat", label: "Sat", bigLabel: "Saturday" },
  { key: "Sun", label: "Sun", bigLabel: "Sunday" },
];

const ChooseSections = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { sections } = useSections();
  const [currentCourseIndex, setCurrentCourseIndex] = useState(
    state?.nextCourseIndex || 0
  );
  const [courseData, setCourseData] = useState(state?.allCourseData || {});

  // Get list of uploaded files that need manual setup (no sections or empty sections)
  const coursesNeedingSetup = useMemo(() => {
    const fileNames = Object.keys(sections);
    return fileNames.filter((fileName) => {
      const course = sections[fileName];
      return !course.sections || course.sections.length === 0;
    });
  }, [sections]);

  const currentFile = coursesNeedingSetup[currentCourseIndex];
  const isLastCourse = currentCourseIndex === coursesNeedingSetup.length - 1;

  // Get current course info
  const currentCourse = sections[currentFile];
  const courseName = currentCourse?.course_name || "";
  const displayName = courseName || currentFile?.replace("uploads/", "") || "";

  // Form state for current course
  const [formData, setFormData] = useState({
    courseName: "",
    courseCode: "",
    selectedDays: [],
    isAsynchronous: false,
  });

  // Error states
  const [errors, setErrors] = useState({
    courseName: "",
    selectedDays: "",
  });

  // Handle navigation redirects
  useEffect(() => {
    if (Object.keys(sections).length === 0) {
      navigate("/upload");
      return;
    }

    // If no courses need manual setup, go directly to section selection
    if (coursesNeedingSetup.length === 0) {
      navigate("/select-sections");
      return;
    }
  }, [sections, coursesNeedingSetup.length, navigate]);

  // Handle form data updates
  useEffect(() => {
    if (!currentFile) return;

    // Load existing data for current course if available
    if (courseData[currentFile]) {
      setFormData(courseData[currentFile]);
    } else {
      // Reset form for new course
      setFormData({
        courseName: courseName || "", // Pre-fill with detected course name
        courseCode: "",
        selectedDays: [],
        isAsynchronous: false,
      });
    }

    // Clear errors when switching courses
    setErrors({
      courseName: "",
      selectedDays: "",
    });
  }, [currentFile, courseData, courseName]);

  const handleInputChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  // Asynchronous toggle handler
  const handleAsynchToggle = () => {
    const newIsAsynchronous = !formData.isAsynchronous;
    setFormData({
      ...formData,
      isAsynchronous: newIsAsynchronous,
      selectedDays: newIsAsynchronous ? [] : formData.selectedDays,
    });

    // Clear selected days if switching to asynchronous
    if (newIsAsynchronous) {
      setErrors({
        ...errors,
        selectedDays: "",
      });
    }
  };

  const handleDayToggle = (dayKey) => {
    
    // If asynchronous don't allow day selection
    if (formData.isAsynchronous) {
      return;
    }

    const updatedDays = formData.selectedDays.includes(dayKey)
      ? formData.selectedDays.filter((day) => day !== dayKey)
      : [...formData.selectedDays, dayKey];

    setFormData({
      ...formData,
      selectedDays: updatedDays,
    });

    // Clear selectedDays error when user selects a day
    if (updatedDays.length > 0 && errors.selectedDays) {
      setErrors({
        ...errors,
        selectedDays: "",
      });
    }
  };

  const handleProceedToStep2 = () => {
    // Reset errors
    let newErrors = {
      courseName: "",
      selectedDays: "",
    };
  
    let hasErrors = false;
  
    // Validate course name
    if (!formData.courseName.trim()) {
      newErrors.courseName = "Please enter a course name";
      hasErrors = true;
    }
  
    // Validate selected days (if not asynchronous)
    if (formData.selectedDays.length === 0 && !formData.isAsynchronous) {
      newErrors.selectedDays = "Please select at least one day";
      hasErrors = true;
    }
  
    setErrors(newErrors);
  
    if (hasErrors) {
      return;
    }
  
    // Save current course data
    const updatedCourseData = {
      ...courseData,
      [currentFile]: formData,
    };
    setCourseData(updatedCourseData);
  
    // Also save to localStorage for persistence
    localStorage.setItem("userCourseData", JSON.stringify(updatedCourseData));
    sessionStorage.setItem("userCourseData", JSON.stringify(updatedCourseData));
  
    // Check for other courses (sync courses from sections or other processed courses)
    const hasOtherCourses = Object.keys(sections).some(fileName => 
      fileName !== currentFile && 
      (sections[fileName].sections?.length > 0 || updatedCourseData[fileName])
    );
    
    console.log("hasOtherCourses: ", hasOtherCourses);
    console.log("sections: ", sections);
    console.log("updatedCourseData: ", updatedCourseData);
  
    // Skip to calendar selection if asynchronous
    if (formData.isAsynchronous) {
      if (isLastCourse) {
        // Build the sections data structure that backend expects
        const allSectionsData = {};
        const selectedSections = {};
        
        // First, add ALL courses from sections (sync courses that were auto-detected)
        Object.entries(sections).forEach(([fileName, sectionData]) => {
          if (sectionData.sections?.length > 0 && !updatedCourseData[fileName]) {
            // This is a sync course with auto-detected sections that wasn't manually configured
            allSectionsData[fileName] = sectionData;
            // Select the first section by default
            if (sectionData.sections[0]) {
              selectedSections[fileName] = {
                ...sectionData.sections[0],
                course_name: sectionData.course_name,
                pdf_path: fileName
              };
            }
          }
        });
        
        // Then add all manually configured courses (both async and sync)
        Object.entries(updatedCourseData).forEach(([fileName, courseData]) => {
          if (courseData.isAsynchronous) {
            // Structure for async courses
            allSectionsData[fileName] = {
              course_name: courseData.courseName,
              pdf: fileName,
              sections: [{
                name: "Asynchronous",
                timing: null,
                isAsynchronous: true
              }]
            };
            
            // Set the selected section for async
            selectedSections[fileName] = {
              name: "Asynchronous",
              timing: null,
              isAsynchronous: true,
              course_name: courseData.courseName,
              pdf_path: fileName
            };
          } else if (courseData.courseTimes) {
            // This is a manually configured sync course
            // It should already be in localStorage from add-course-times
            // But we need to ensure it's in the allSectionsData
            const userSections = Object.entries(courseData.courseTimes).map(([day, times]) => ({
              name: `${courseData.courseName} - ${day}`,
              timing: `${times.from} - ${times.to}`,
              day: day,
              isUserCreated: true
            }));
            
            allSectionsData[fileName] = {
              course_name: courseData.courseName,
              pdf: fileName,
              sections: userSections
            };
            
            // Select first section by default
            if (userSections[0]) {
              selectedSections[fileName] = {
                ...userSections[0],
                course_name: courseData.courseName,
                pdf_path: fileName
              };
            }
          }
        });
        
        // Save to storage so the backend can access it
        localStorage.setItem("allSectionsData", JSON.stringify(allSectionsData));
        sessionStorage.setItem("allSectionsData", JSON.stringify(allSectionsData));
        localStorage.setItem("selectedSections", JSON.stringify(selectedSections));
        sessionStorage.setItem("selectedSections", JSON.stringify(selectedSections));
        
        if (!hasOtherCourses) {
          // Only async courses, go directly to calendar
          navigate("/select-calendar", {
            state: {
              hasAsyncCourses: true
            }
          });
        } else {
          // Has other courses, go to select-sections to review all
          navigate("/select-sections");
        }
      } else {
        setCurrentCourseIndex(currentCourseIndex + 1);
      }
    } else {
      // Navigate to step 2 with current course data
      navigate("/add-course-times", {
        state: {
          currentFile,
          formData,
          currentCourseIndex,
          isLastCourse,
          allCourseData: updatedCourseData,
        },
      });
    }
  };
  const handleExcludeCourse = () => {
    if (isLastCourse) {
      // If this was the last course, proceed to section selection
      navigate("/select-sections");
    } else {
      // Move to next course
      setCurrentCourseIndex(currentCourseIndex + 1);
    }
  };

  if (!currentFile || coursesNeedingSetup.length === 0) {
    return (
      <PageContainer>
        <div className={s.container}>
          <PageHeader title="No courses to configure" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className={s.container}>
        <PageHeader
          title="Add course title and section"
          description={`Course title and/or section for "${displayName}".`}
          step={formData.isAsynchronous ? "" : "Step 1 of 2"}
        />

        <form className={s.courseForm}>
          <div className={s.formField}>
            <label className={s.fieldLabel}>Course name</label>
            <Input
              type="text"
              name="courseName"
              value={formData.courseName}
              onChange={(value) => handleInputChange("courseName", value)}
              placeholder="e.g. Biology 101"
              required
              error={errors.courseName}
            />
            <label className={s.hits}>Supportive text</label>
          </div>

          <div className={s.formField}>
            <label className={s.fieldLabel}>
              Course code{" "}
              <p className={s["fieldLabel--optional"]}>(optional)</p>
            </label>
            <Input
              type="text"
              name="courseCode"
              value={formData.courseCode}
              onChange={(value) => handleInputChange("courseCode", value)}
              placeholder="e.g. A, B, C or 001"
            />
            <label className={s.hits}>Supportive text</label>
          </div>

          <div className={s.formField}>
            <label className={s.checkboxContainer}>
              <span>Asynchronous</span>
              <input
                type="checkbox"
                className={s.customCheckbox}
                checked={formData.isAsynchronous}
                onChange={handleAsynchToggle}
              />
              <span className={s.checkmark}></span>
            </label>
          </div>

          <div className={s.formField}>
            <label className={s.fieldLabel}>Choose meeting days</label>
            <div
              className={`${s.daysSelector} ${
                errors.selectedDays && s["daysSelector--error"]
              } ${formData.isAsynchronous && s["daysSelector--disabled"]}`} 
            >
              {days.map((day) => (
                <button
                  key={day.key}
                  type="button"
                  className={`${s.dayButton} ${
                    formData.selectedDays.includes(day.key) &&
                    s["dayButton--selected"]
                  } ${formData.isAsynchronous && s["dayButton--disabled"]}`}
                  onClick={() => handleDayToggle(day.key)}
                  disabled={formData.isAsynchronous}
                >
                  <span className={s.smallLabel}>{day.label}</span>
                  <span className={s.bigLabel}>{day.bigLabel}</span>
                </button>
              ))}
            </div>
            {errors.selectedDays && (
              <div className={s.fieldError}>{errors.selectedDays}</div>
            )}
            {formData.isAsynchronous && (
              <div className={s.asyncNote}>
                Meeting day selection is disabled for asynchronous courses.
              </div>
            )}
          </div>
        </form>

        <div className={s.formActions}>
          <Button
            variant="primary"
            onClick={handleProceedToStep2}
            className={s.proceedButton}
          >
            {formData.isAsynchronous ? "Save and continue" : "Proceed to next step"}
          </Button>

          <Button
            variant="secondary"
            onClick={handleExcludeCourse}
            className={s.excludeButton}
          >
            Exclude course
          </Button>
        </div>
      </div>
    </PageContainer>
  );
};

export default ChooseSections;
